import {
  Anchor,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconCheck, IconFileText, IconX } from '@tabler/icons-react';
import { Link, useParams } from 'react-router-dom';
import { BillingBreadcrumb } from './components/BillingBreadcrumb';
import { BillingDetailItem } from './components/BillingDetailItem';
import { ClaimBreakdown } from './components/ClaimBreakdown';
import { ClaimTimeline } from './components/ClaimTimeline';
import { formatCurrency, formatDate, titleCase } from './components/billingFormatters';
import { RCM_STATUS_COLORS, rcmStatusLabel } from './components/rcmStatus';
import { claimTotal, findClaimById, type ClaimStatus } from './mockClaims';

const STATUS_COLORS: Record<ClaimStatus, string> = {
  active: 'green',
  draft: 'gray',
  cancelled: 'red',
  'entered-in-error': 'orange',
};

export function ClaimDetailPage(): JSX.Element {
  const { claimId } = useParams();
  const claim = findClaimById(claimId);

  if (!claim) {
    return (
      <Stack gap="md" py="xl">
        <BillingBreadcrumb backTo="/dashboard/billing/claims" backLabel="Back to claims" />
        <Text c="dimmed">We couldn&apos;t find a claim with id &ldquo;{claimId}&rdquo;.</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <BillingBreadcrumb backTo="/dashboard/billing/claims" backLabel="Back to claims" />

      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={3}>{claim.patientName}</Title>
          <Text size="sm" c="dimmed">
            {claim.identifier} · {claim.payer}
          </Text>
        </Stack>
        <Group gap="xs">
          <Badge variant="light" color={STATUS_COLORS[claim.status]}>
            {titleCase(claim.status)}
          </Badge>
          <Badge variant="light" color={RCM_STATUS_COLORS[claim.rcmStatus]}>
            {rcmStatusLabel(claim.rcmStatus)}
          </Badge>
        </Group>
      </Group>

      <Group gap="sm">
        <Button
          component={Link}
          to={`/dashboard/billing/claims/${encodeURIComponent(claim.id)}/cms1500`}
          variant="light"
          leftSection={<IconFileText size={16} />}
        >
          Edit CMS-1500 / Resubmit
        </Button>
        {claim.eobId && (
          <Button
            component={Link}
            to={`/dashboard/billing/eobs/${encodeURIComponent(claim.eobId)}`}
            variant="light"
          >
            View EOB
          </Button>
        )}
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
        <BillingDetailItem label="Total Charge" value={formatCurrency(claimTotal(claim))} />
        <BillingDetailItem label="Provider" value={claim.provider} />
        <BillingDetailItem label="Stedi Correlation ID" value={claim.stediCorrelationId ?? '—'} />
        <BillingDetailItem label="Payer Claim #" value={claim.payerClaimNumber ?? '—'} />
        <BillingDetailItem label="Subscriber ID" value={claim.subscriberId ?? '—'} />
        <BillingDetailItem
          label="Diagnosis"
          value={claim.diagnosisCodes?.join(', ') ?? '—'}
        />
        <BillingDetailItem label="Created" value={formatDate(claim.created)} />
        <BillingDetailItem label="Use" value={titleCase(claim.use)} />
      </SimpleGrid>

      {claim.readinessChecks && claim.readinessChecks.length > 0 && (
        <Card withBorder padding="md">
          <Stack gap="sm">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Readiness Checklist
            </Text>
            {claim.readinessChecks.map((check) => (
              <Group key={check.label} gap="xs">
                {check.passed ? (
                  <IconCheck size={16} color="var(--mantine-color-green-6)" />
                ) : (
                  <IconX size={16} color="var(--mantine-color-red-6)" />
                )}
                <Text size="sm">{check.label}</Text>
              </Group>
            ))}
          </Stack>
        </Card>
      )}

      {claim.timeline && claim.timeline.length > 0 && (
        <Card withBorder padding="md">
          <ClaimTimeline events={claim.timeline} />
        </Card>
      )}

      <Card withBorder padding="md">
        <ClaimBreakdown claim={claim} />
      </Card>

      {claim.accountId && (
        <>
          <Divider />
          <Anchor component={Link} to="/dashboard/billing/accounts" size="sm" fw={600}>
            View patient account ledger
          </Anchor>
        </>
      )}
    </Stack>
  );
}
